/* Kkumi universal async-button busy state — the ONE place that toggles a
 * button into / out of its loading state. Exposed as window.KKBtn.
 *
 * Why a real injected <span> spinner and not a CSS `::before`: Dawn's
 * base.css kills every `.button::before` (`content:none; display:none`) AND
 * uses another `.button::before` as an absolutely-positioned drop-shadow
 * plate (inset:0; z-index:-1). A pseudo-element spinner has to out-specify
 * and undo all of that and still loses on some buttons. A real child element
 * is immune to that cascade, lays out inline with the label via the button's
 * own `display:inline-flex; gap`, and works on every `.button`-family CTA.
 *
 * Usage:
 *   KKBtn.busy(btn, 'Redeeming…')   // disable + aria-busy + spinner + label
 *   KKBtn.relabel(btn, 'Adding…')   // swap the label, keep the spinner
 *   KKBtn.reset(btn)                // restore original markup + enable
 *
 * THE FOOTPRINT IS FROZEN. A busy label is nearly always longer than the idle
 * one ("Redeem" -> spinner + "Redeeming…"), so an unconstrained busy button
 * grows and reflows the row it sits in — on the cart wallet's redeem panel it
 * pushed the whole control onto a second line mid-action, which reads as a
 * glitch at exactly the moment the shopper is waiting on money. So busy()
 * pins the button to its idle box, and when the label no longer fits inside
 * it, drops to a SPINNER-ONLY button rather than overflowing or wrapping. The
 * label is never lost to assistive tech — it moves to `aria-label`, and
 * `aria-busy` already announces the state. Wide buttons (add to cart) are
 * unaffected: their label fits, so it stays visible.
 *
 * Always pair busy() with reset() on BOTH success and failure (a .finally is
 * the safe spot). See FRONTEND.md "Async buttons". */
(function () {
  if (window.KKBtn) return;

  function spinnerEl() {
    var sp = document.createElement('span');
    sp.className = 'kk-spinner';
    sp.setAttribute('aria-hidden', 'true');
    return sp;
  }

  // Measure the button's rendered box BEFORE we touch its contents. Returns 0
  // when it isn't laid out (detached, display:none) — callers then skip the
  // freeze entirely rather than pinning a button to a bogus width.
  function idleWidth(btn) {
    var r = btn.getBoundingClientRect();
    return (r && r.width > 1) ? r.width : 0;
  }

  // Pin the button to `w` — the value getBoundingClientRect() reports, i.e. a
  // BORDER-box figure. This theme has no global `* { box-sizing: border-box }`
  // reset (see the note in assets/kkumi-bias-typeahead.css), so buttons are
  // content-box unless a rule says otherwise, and a bare `width: <rect>px`
  // would ADD the padding and border on top — growing the button instead of
  // pinning it, the exact bug this is here to prevent. Setting box-sizing
  // inline alongside the width makes the pin exact whatever the button's own
  // box model is; unfreeze() puts the original declarations back.
  //
  // A `width: 100% !important` full-width button out-specifies this, which is
  // correct — it can't grow anyway.
  function freeze(btn, w) {
    btn.__kkPrevWidth = btn.style.width;
    btn.__kkPrevMinWidth = btn.style.minWidth;
    btn.__kkPrevBoxSizing = btn.style.boxSizing;
    btn.style.boxSizing = 'border-box';
    btn.style.width = w + 'px';
    btn.style.minWidth = w + 'px';
  }

  function unfreeze(btn) {
    if (btn.__kkPrevWidth != null) { btn.style.width = btn.__kkPrevWidth; btn.__kkPrevWidth = null; }
    if (btn.__kkPrevMinWidth != null) { btn.style.minWidth = btn.__kkPrevMinWidth; btn.__kkPrevMinWidth = null; }
    if (btn.__kkPrevBoxSizing != null) { btn.style.boxSizing = btn.__kkPrevBoxSizing; btn.__kkPrevBoxSizing = null; }
  }

  // Append the label, then keep it only if it fits inside the frozen box. The
  // text is an anonymous flex item whose default `min-width: auto` stops it
  // shrinking below min-content, so an oversized label overflows and shows up
  // in scrollWidth — that's the signal to fall back to spinner-only.
  function setLabel(btn, label, frozen) {
    if (btn.__kkAriaAdded) { btn.removeAttribute('aria-label'); btn.__kkAriaAdded = false; }
    if (!label) return;
    btn.appendChild(document.createTextNode(label));
    if (!frozen) return;
    if (btn.scrollWidth > Math.ceil(btn.clientWidth) + 1) {
      // Doesn't fit — spinner only, label preserved for assistive tech.
      while (btn.childNodes.length > 1) btn.removeChild(btn.lastChild);
      if (!btn.getAttribute('aria-label')) { btn.setAttribute('aria-label', label); btn.__kkAriaAdded = true; }
    }
  }

  function busy(btn, label) {
    if (!btn || btn.getAttribute('aria-busy') === 'true') return;
    // Stash the original markup so reset() can restore it verbatim (this is
    // the button's own trusted content, never anything API-controlled).
    btn.__kkLabelHTML = btn.innerHTML;
    var w = idleWidth(btn);
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    while (btn.firstChild) btn.removeChild(btn.firstChild);
    btn.appendChild(spinnerEl());
    if (w) freeze(btn, w);
    setLabel(btn, label, !!w);
  }

  // Swap the trailing label while staying busy (e.g. "Redeeming…" →
  // "Adding to cart…") without dropping the spinner. The box stays frozen at
  // the idle width, so a longer second label can't reflow the row either.
  function relabel(btn, label) {
    if (!btn || btn.getAttribute('aria-busy') !== 'true') return;
    // Keep the spinner (first child); drop everything after it.
    while (btn.childNodes.length > 1) btn.removeChild(btn.lastChild);
    setLabel(btn, label, btn.__kkPrevWidth != null);
  }

  function reset(btn) {
    if (!btn || btn.getAttribute('aria-busy') !== 'true') return;
    if (btn.__kkLabelHTML != null) { btn.innerHTML = btn.__kkLabelHTML; btn.__kkLabelHTML = null; }
    if (btn.__kkAriaAdded) { btn.removeAttribute('aria-label'); btn.__kkAriaAdded = false; }
    unfreeze(btn);
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
  }

  window.KKBtn = { busy: busy, relabel: relabel, reset: reset, spinnerEl: spinnerEl };
})();
